import {useTheme} from "@mui/material";
import type {Breakpoint} from "@mui/material";
import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";

import useKeepContentScrollPosition from "../../utils/scrolling/useKeepContentScrollPosition";
import useMobileMode from "../../hooks/useMobileMode";
import selectChapterExercise from "../../store/chapterExercises/selectChapterExercise";
import {useAppSelector} from "../../store/hooks";
import {scrollMinDelay} from "../../utils/scrolling/const";
import ScrollContext from "../../utils/scrolling/ScrollContext";

export enum SidebarType {
	None,
	AskTutor,
	Answer,
	ReportIssue,
}

function noop() {
	return;
}

const SidebarContext = React.createContext<{
	type: SidebarType;
	chapterExerciseKey: string;
	fullscreen: boolean;
	mobileMode: boolean;
	open: (sidebarType: SidebarType, chapterExerciseKey?: string) => void;
	close: () => void;
	toggleFullscreen: () => void;
}>({
	type: SidebarType.None,
	chapterExerciseKey: "",
	fullscreen: false,
	mobileMode: false,
	open: noop,
	close: noop,
	toggleFullscreen: noop,
});

function SidebarContextProvider(props: {
	children: React.ReactElement | React.ReactElement[];
	mobileModeBreakppoint: Breakpoint;
}): JSX.Element {
	const [type, setType] = useState(SidebarType.None);
	const [chapterExerciseKey, setChapterExerciseKey] = useState("");
	const [fullscreen, setFullscreen] = useState(false);

	const exerciseId = useAppSelector(
		(state) => selectChapterExercise(state, chapterExerciseKey).id
	);

	const mobileMode = useMobileMode(props.mobileModeBreakppoint);
	const theme = useTheme();

	const {pushElementToViewport} = useContext(ScrollContext);

	const [keepContentScrollPosition] = useKeepContentScrollPosition();

	const status = useRef<"closed" | "closing" | "open" | "opening">("closed");

	const open = useCallback((sidebarType: SidebarType, key?: string) => {
		setType(sidebarType);
		setChapterExerciseKey(key ?? "");

		if (status.current !== "open") {
			status.current = "opening";
		}
	}, []);

	const close = useCallback(() => {
		setType(SidebarType.None);
		setChapterExerciseKey("");

		if (status.current !== "closed") {
			status.current = "closing";
		}
	}, []);

	const toggleFullscreen = useCallback(() => {
		setFullscreen((prev) => !prev);
	}, []);

	const shouldScroll = !mobileMode && !fullscreen;

	useEffect(() => {
		let t: ReturnType<typeof setTimeout>;

		if (status.current === "opening") {
			if (shouldScroll) {
				t = setTimeout(() => {
					pushElementToViewport(`subsection-${exerciseId}`, {bottom: 0.3});
				}, theme.transitions.duration.leavingScreen + scrollMinDelay);
			}

			status.current = "open";
		}

		if (status.current === "closing") {
			if (shouldScroll) {
				t = keepContentScrollPosition(
					theme.transitions.duration.enteringScreen + scrollMinDelay
				);
			}

			status.current = "closed";
		}

		return () => clearTimeout(t);
	}, [
		exerciseId,
		keepContentScrollPosition,
		pushElementToViewport,
		shouldScroll,
		theme.transitions.duration.enteringScreen,
		theme.transitions.duration.leavingScreen,
	]);

	const value = useMemo(
		() => ({
			type,
			chapterExerciseKey,
			fullscreen,
			mobileMode,
			open,
			close,
			toggleFullscreen,
		}),
		[
			chapterExerciseKey,
			close,
			fullscreen,
			mobileMode,
			open,
			toggleFullscreen,
			type,
		]
	);

	return (
		<SidebarContext.Provider value={value}>
			{props.children}
		</SidebarContext.Provider>
	);
}

export default SidebarContext;
export {SidebarContextProvider};
